/**
 * DRF Recovery Optimizer - Attempts to recover non-viable candidates with negative DRF
 *
 * RESPONSIBILITY:
 * Recovers candidates that fail due to negative DRF balance by reducing principal
 * in earlier payments. This is a "rescue" operation for failed candidates.
 *
 * HIERARCHY:
 * - Used by: ConfigurationOptimizer.attemptRecovery() (Level 3)
 * - Uses: PrincipalBasedRecalculationEngine, ProcessingFinalizer
 * - Peers: FiveYearRuleOptimizer, UtilizationOptimizer (but runs on NON-VIABLE candidates)
 *
 * KEY OPERATIONS:
 * - detectRecoveryPotential: Check if candidate can be recovered
 * - findMostNegativeDrf: Locate the worst DRF violation
 * - findSourcePayments: Find payments with principal to reduce
 * - pullPrincipal: Remove principal and recalculate interest
 * - attemptRecovery: Main entry point, iterates up to maxIterations
 *
 * IMPLEMENTATION NOTES:
 * - Only runs on NON-VIABLE candidates (opposite of other optimizers)
 * - Pulls principal in $5,000 increments (ceiling rounded)
 * - Reduces principal from earliest payments first
 * - Maximum 2 iterations by default
 */

import { logger } from '~/services/logging';
import type { Candidate, Configuration } from '~/types';
import { PrincipalBasedRecalculationEngine } from '../../engines/cashflow-engine/principal-recalculator';
import { ProcessingFinalizer } from '../../engines/cashflow-engine/processing-finalizer';
import { CandidateNaming } from '../../shared/candidate-naming';

/**
 * Configuration for DRF recovery optimization
 */
export interface DRFRecoveryConfig {
  /** Principal reduction increment (default: $5,000) */
  principalIncrement?: number;

  /** Maximum recovery iterations (default: 2) */
  maxIterations?: number;

  /** Enable detailed logging */
  enableLogging?: boolean;
}

/**
 * Analysis of recovery potential
 */
export interface RecoveryAnalysis {
  /** Whether recovery is possible */
  canRecover: boolean;

  /** Reason for analysis result */
  reason: string;

  /** Most negative DRF balance found */
  mostNegativeDrf: number;

  /** Payment date with worst DRF */
  worstPaymentDate: string | null;

  /** Amount needed to recover (ceiling rounded) */
  recoveryAmountNeeded: number;

  /** Total principal available in earlier payments */
  availablePrincipal: number;
}

/**
 * Result of recovery attempt
 */
export interface RecoveryResult {
  /** Whether recovery succeeded */
  success: boolean;

  /** Recovered candidate (if successful) */
  recoveredCandidate: Candidate | null;

  /** Original candidate */
  originalCandidate: Candidate;

  /** Number of iterations performed */
  iterationsPerformed: number;

  /** Description of what happened */
  description: string;

  /** Details of adjustments made */
  adjustments: Array<{
    iteration: number;
    paymentDate: string;
    principalReduced: number;
    bondSeriesId: string;
  }>;
}

/**
 * Information about a source payment for principal reduction
 */
interface SourcePaymentInfo {
  paymentDate: string;
  bondSeriesId: string;
  availablePrincipal: number;
}

export class DRFRecoveryOptimizer {
  private static readonly DEFAULT_CONFIG: Required<DRFRecoveryConfig> = {
    principalIncrement: 5000,
    maxIterations: 2,
    enableLogging: true,
  };

  /**
   * Detects if a candidate can potentially be recovered from negative DRF
   */
  static detectRecoveryPotential(
    candidate: Candidate,
    config: DRFRecoveryConfig = {}
  ): RecoveryAnalysis {
    const mergedConfig = { ...DRFRecoveryOptimizer.DEFAULT_CONFIG, ...config };

    // Find the most negative DRF balance
    const { mostNegativeDrf, worstPaymentDate, worstPaymentIndex } =
      DRFRecoveryOptimizer.findMostNegativeDrf(candidate);

    // If no negative DRF, no recovery needed
    if (mostNegativeDrf >= 0) {
      return {
        canRecover: false,
        reason: 'No negative DRF balance found - candidate may have other issues',
        mostNegativeDrf: 0,
        worstPaymentDate: null,
        recoveryAmountNeeded: 0,
        availablePrincipal: 0,
      };
    }

    // Calculate recovery amount needed (ceiling to increment)
    const recoveryAmountNeeded = DRFRecoveryOptimizer.ceilingToIncrement(
      Math.abs(mostNegativeDrf),
      mergedConfig.principalIncrement
    );

    // Find available principal in payments before the worst DRF
    const availablePrincipal = DRFRecoveryOptimizer.calculateAvailablePrincipal(
      candidate,
      worstPaymentIndex
    );

    const canRecover = availablePrincipal >= recoveryAmountNeeded;

    if (mergedConfig.enableLogging) {
      logger.L3.info(`[DRF RECOVERY] Analysis for ${candidate.name}`, {
        mostNegativeDrf,
        worstPaymentDate,
        recoveryAmountNeeded,
        availablePrincipal,
        canRecover,
      });
    }

    return {
      canRecover,
      reason: canRecover
        ? `Can recover: need $${recoveryAmountNeeded.toLocaleString()}, have $${availablePrincipal.toLocaleString()} available`
        : `Cannot recover: need $${recoveryAmountNeeded.toLocaleString()}, only $${availablePrincipal.toLocaleString()} available`,
      mostNegativeDrf,
      worstPaymentDate,
      recoveryAmountNeeded,
      availablePrincipal,
    };
  }

  /**
   * Attempts to recover a non-viable candidate with negative DRF
   */
  static attemptRecovery(
    candidate: Candidate,
    configuration: Configuration,
    config: DRFRecoveryConfig = {}
  ): RecoveryResult {
    const mergedConfig = { ...DRFRecoveryOptimizer.DEFAULT_CONFIG, ...config };

    if (mergedConfig.enableLogging) {
      logger.L3.info(`[DRF RECOVERY] Starting recovery attempt for ${candidate.name}`);
    }

    // Initial analysis
    const analysis = DRFRecoveryOptimizer.detectRecoveryPotential(candidate, config);

    if (!analysis.canRecover) {
      return {
        success: false,
        recoveredCandidate: null,
        originalCandidate: candidate,
        iterationsPerformed: 0,
        description: analysis.reason,
        adjustments: [],
      };
    }

    let currentCandidate = candidate;
    const adjustments: RecoveryResult['adjustments'] = [];

    // Iterate up to maxIterations
    for (let iteration = 1; iteration <= mergedConfig.maxIterations; iteration++) {
      if (mergedConfig.enableLogging) {
        logger.L3.info(`[DRF RECOVERY] Iteration ${iteration}/${mergedConfig.maxIterations}`);
      }

      // Re-analyze current state
      const currentAnalysis = DRFRecoveryOptimizer.detectRecoveryPotential(
        currentCandidate,
        config
      );

      // If no more negative DRF, we're done
      if (currentAnalysis.mostNegativeDrf >= 0) {
        if (mergedConfig.enableLogging) {
          logger.L3.info(
            `[DRF RECOVERY] Success! No more negative DRF after ${iteration - 1} iterations`
          );
        }
        return {
          success: true,
          recoveredCandidate: currentCandidate,
          originalCandidate: candidate,
          iterationsPerformed: iteration - 1,
          description: `Successfully recovered candidate after ${iteration - 1} iteration(s)`,
          adjustments,
        };
      }

      // Attempt recovery for this iteration
      const iterationResult = DRFRecoveryOptimizer.executeRecoveryIteration(
        currentCandidate,
        currentAnalysis,
        configuration,
        iteration,
        mergedConfig
      );

      if (!iterationResult.success) {
        if (mergedConfig.enableLogging) {
          logger.L3.warn(`[DRF RECOVERY] Iteration ${iteration} failed: ${iterationResult.reason}`);
        }
        break;
      }

      // Update for next iteration
      currentCandidate = iterationResult.candidate!;
      adjustments.push(...iterationResult.adjustments);
    }

    // Final check - is the candidate now viable?
    const finalAnalysis = DRFRecoveryOptimizer.detectRecoveryPotential(currentCandidate, config);
    const isNowViable = finalAnalysis.mostNegativeDrf >= 0 && currentCandidate.results?.isViable;

    if (isNowViable) {
      if (mergedConfig.enableLogging) {
        logger.L3.info(
          `[DRF RECOVERY] Recovery successful after ${adjustments.length} adjustment(s)`
        );
      }
      return {
        success: true,
        recoveredCandidate: currentCandidate,
        originalCandidate: candidate,
        iterationsPerformed: adjustments.length,
        description: `Successfully recovered candidate with ${adjustments.length} principal reduction(s)`,
        adjustments,
      };
    }

    if (mergedConfig.enableLogging) {
      logger.L3.warn(`[DRF RECOVERY] Recovery failed - candidate still has issues`);
    }

    return {
      success: false,
      recoveredCandidate: null,
      originalCandidate: candidate,
      iterationsPerformed: adjustments.length,
      description: `Recovery incomplete - still have negative DRF of $${Math.abs(finalAnalysis.mostNegativeDrf).toLocaleString()}`,
      adjustments,
    };
  }

  /**
   * Executes a single recovery iteration
   */
  private static executeRecoveryIteration(
    candidate: Candidate,
    analysis: RecoveryAnalysis,
    configuration: Configuration,
    iteration: number,
    config: Required<DRFRecoveryConfig>
  ): {
    success: boolean;
    candidate: Candidate | null;
    reason: string;
    adjustments: RecoveryResult['adjustments'];
  } {
    // Find source payments to pull from
    const { worstPaymentIndex } = DRFRecoveryOptimizer.findMostNegativeDrf(candidate);
    const sourcePayments = DRFRecoveryOptimizer.findSourcePayments(candidate, worstPaymentIndex);

    if (sourcePayments.length === 0) {
      return {
        success: false,
        candidate: null,
        reason: 'No source payments with principal found',
        adjustments: [],
      };
    }

    // Create a copy of the candidate for modification
    const recoveryCandidate = DRFRecoveryOptimizer.createRecoveryCandidate(candidate);

    let remainingToRecover = analysis.recoveryAmountNeeded;
    const adjustments: RecoveryResult['adjustments'] = [];

    // Pull principal from source payments (earliest first)
    for (const source of sourcePayments) {
      if (remainingToRecover <= 0) break;

      const pullAmount = Math.min(source.availablePrincipal, remainingToRecover);
      // Round to increment
      const roundedPullAmount = DRFRecoveryOptimizer.ceilingToIncrement(
        pullAmount,
        config.principalIncrement
      );

      // Find and modify the bond payment
      const cashFlow = recoveryCandidate.cashFlows.find(
        (cf) => cf.paymentDate === source.paymentDate
      );
      const bondPayment = cashFlow?.bondPayments.find(
        (bp) => bp.bondSeriesId === source.bondSeriesId
      );

      if (cashFlow && bondPayment && bondPayment.principal >= roundedPullAmount) {
        // Reduce principal
        bondPayment.principal -= roundedPullAmount;
        remainingToRecover -= roundedPullAmount;

        adjustments.push({
          iteration,
          paymentDate: source.paymentDate,
          principalReduced: roundedPullAmount,
          bondSeriesId: source.bondSeriesId,
        });

        if (config.enableLogging) {
          logger.L3.info(
            `[DRF RECOVERY] Reduced principal by $${roundedPullAmount.toLocaleString()} ` +
              `from ${source.paymentDate} (${source.bondSeriesId})`
          );
        }
      }
    }

    if (adjustments.length === 0) {
      return {
        success: false,
        candidate: null,
        reason: 'Could not reduce any principal',
        adjustments: [],
      };
    }

    // Add optimization history entry
    if (!recoveryCandidate.optimizationHistory) {
      recoveryCandidate.optimizationHistory = [];
    }

    const totalReduced = adjustments.reduce((sum, adj) => sum + adj.principalReduced, 0);
    recoveryCandidate.optimizationHistory.push({
      type: 'drf-recovery',
      description: `Reduced principal by $${totalReduced.toLocaleString()} to recover from negative DRF`,
      timestamp: new Date().toISOString(),
      details: {
        iteration,
        adjustments,
        targetDrf: analysis.mostNegativeDrf,
        recoveryAmountNeeded: analysis.recoveryAmountNeeded,
      },
    });

    // Recalculate interest cascading effects
    PrincipalBasedRecalculationEngine.recalculateFromPrincipal(
      recoveryCandidate,
      configuration.bondSeries
    );

    // Finalize the candidate (runs forward pass, recalculates results)
    const finalizationResult = ProcessingFinalizer.finalizeAfterOptimization(
      recoveryCandidate,
      configuration
    );

    if (!finalizationResult.success) {
      return {
        success: false,
        candidate: null,
        reason: `Finalization failed: ${finalizationResult.error || 'unknown error'}`,
        adjustments,
      };
    }

    return {
      success: true,
      candidate: recoveryCandidate,
      reason: 'Successfully applied principal reduction',
      adjustments,
    };
  }

  /**
   * Finds the most negative DRF ending balance in the candidate
   */
  private static findMostNegativeDrf(candidate: Candidate): {
    mostNegativeDrf: number;
    worstPaymentDate: string | null;
    worstPaymentIndex: number;
  } {
    let mostNegativeDrf = 0;
    let worstPaymentDate: string | null = null;
    let worstPaymentIndex = -1;

    for (let i = 0; i < candidate.cashFlows.length; i++) {
      const cf = candidate.cashFlows[i];
      if (cf.drfEndingBalance < mostNegativeDrf) {
        mostNegativeDrf = cf.drfEndingBalance;
        worstPaymentDate = cf.paymentDate;
        worstPaymentIndex = i;
      }
    }

    return { mostNegativeDrf, worstPaymentDate, worstPaymentIndex };
  }

  /**
   * Calculates total available principal in payments before the worst DRF
   */
  private static calculateAvailablePrincipal(
    candidate: Candidate,
    worstPaymentIndex: number
  ): number {
    let total = 0;

    for (let i = 0; i <= worstPaymentIndex && i < candidate.cashFlows.length; i++) {
      const cf = candidate.cashFlows[i];
      for (const bp of cf.bondPayments) {
        total += bp.principal;
      }
    }

    return total;
  }

  /**
   * Finds source payments with principal that can be reduced
   * Returns payments sorted by date (earliest first)
   */
  private static findSourcePayments(
    candidate: Candidate,
    worstPaymentIndex: number
  ): SourcePaymentInfo[] {
    const sources: SourcePaymentInfo[] = [];

    // Look at payments up to and including the worst payment
    for (let i = 0; i <= worstPaymentIndex && i < candidate.cashFlows.length; i++) {
      const cf = candidate.cashFlows[i];

      for (const bp of cf.bondPayments) {
        if (bp.principal > 0) {
          sources.push({
            paymentDate: cf.paymentDate,
            bondSeriesId: bp.bondSeriesId,
            availablePrincipal: bp.principal,
          });
        }
      }
    }

    // Sort by payment date (earliest first) to prefer reducing early principal
    sources.sort((a, b) => a.paymentDate.localeCompare(b.paymentDate));

    return sources;
  }

  /**
   * Rounds a value up to the nearest increment
   */
  private static ceilingToIncrement(value: number, increment: number): number {
    return Math.ceil(value / increment) * increment;
  }

  /**
   * Creates a deep copy of candidate for recovery modification
   * Must clone all nested objects to prevent reference sharing issues
   */
  private static createRecoveryCandidate(candidate: Candidate): Candidate {
    // Count actual optimizations (excluding initial-shape and optimization-skipped)
    const appliedOptCount =
      candidate.optimizationHistory?.filter(
        (h) => h.type !== 'initial-shape' && h.type !== 'optimization-skipped'
      ).length || 0;
    const newOptCount = appliedOptCount + 1;

    // Use centralized naming: {shapeId}.{count}
    const newName = CandidateNaming.createOptimizationName(candidate.name, newOptCount);

    // Generate new unique ID
    const shapeId = CandidateNaming.getShapeId(candidate.name);

    return {
      ...candidate,
      id: `${shapeId}.${newOptCount}-${crypto.randomUUID().slice(0, 8)}`,
      name: newName,
      // Deep clone cashFlows and nested bondPayments
      cashFlows: candidate.cashFlows.map((cf) => ({
        ...cf,
        bondPayments: cf.bondPayments.map((bp) => ({ ...bp })),
      })),
      // Deep clone optimization history including details objects
      optimizationHistory: candidate.optimizationHistory
        ? [...candidate.optimizationHistory.map((h) => ({ ...h, details: { ...h.details } }))]
        : [],
      // Deep clone revenueMap to prevent mutation
      revenueMap: Object.fromEntries(
        Object.entries(candidate.revenueMap).map(([k, v]) => [
          k,
          { ...v, bondSeriesDetailsByBondId: { ...v.bondSeriesDetailsByBondId } },
        ])
      ),
      // Deep clone millageInformation
      millageInformation: {
        ...candidate.millageInformation,
        millageShape: {
          ...candidate.millageInformation.millageShape,
          intervals: [...candidate.millageInformation.millageShape.intervals],
          adjustments: [...candidate.millageInformation.millageShape.adjustments],
        },
        millageMap: { ...candidate.millageInformation.millageMap },
      },
      // Reset results - will be recalculated during finalization
      results: {
        isViable: false, // Will be determined after finalization
        failureMessages: [],
        totalRevenue: 0,
        totalInterest: 0,
        totalPrincipal: 0,
        revenueUtilization: 0,
        interestRatio: 0,
        efficiency: 0,
        bondSeriesResults: [],
      },
    };
  }
}
